import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import useOffer from 'hooks/Offers/useOffer'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import { kmToMiles } from 'lib/geo/distanceUnits'

const REGIONS_THAT_USE_MILES = ['US', 'GB']
const THRESHOLD_DISTANCE_IN_KM = 10

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function useMetaSearchDistance({ offer, filters }: Props) {
  const intl = useIntl()
  const [metaOffer] = useOffer(filters?.showDistanceWithMetaOfferId, {
    requireSummaryOnly: true,
  })

  const currentRegionCode = useAppSelector((state) => state.geo.currentRegionCode)

  const isMiles = useMemo(() => REGIONS_THAT_USE_MILES.includes(currentRegionCode), [currentRegionCode])

  const metaSearchDistanceToMainOffer = useMemo(() => {
    if (filters?.showDistanceWithMetaOfferId && isBedbankOffer(metaOffer)) {
      const distanceInKm = calculateDistance(offer.property.latitude, offer.property.longitude, metaOffer.property.latitude, metaOffer.property.longitude)
      const distance = isMiles ? kmToMiles(distanceInKm) : distanceInKm
      const result = Math.ceil(distance * 10) / 10
      if (isMiles) {
        return result <= kmToMiles(THRESHOLD_DISTANCE_IN_KM) ? result : 0
      }
      return result <= THRESHOLD_DISTANCE_IN_KM ? result : 0
    }
    return 0
  }, [filters?.showDistanceWithMetaOfferId, offer, metaOffer, isMiles])

  if (metaSearchDistanceToMainOffer === 0) {
    return null
  }

  if (isMiles) {
    return intl.formatMessage(
      {
        id: '2nLY0mPaoi',
        defaultMessage: '{distance} miles from your original search',
        description: 'Distance label in miles from the original meta search property',
      },
      { distance: metaSearchDistanceToMainOffer },
    )
  }
  if (metaSearchDistanceToMainOffer < 1) {
    return intl.formatMessage(
      {
        id: 'Bu7sXM9g75',
        defaultMessage: '{distance} m from your original search',
        description: 'Distance label in metres from the original meta search property',
      },
      { distance: metaSearchDistanceToMainOffer * 1000 },
    )
  }
  return intl.formatMessage(
    {
      id: 'dAY6Qn4xE4',
      defaultMessage: '{distance} km from your original search',
      description: 'Distance label in kilometres from the original meta search property',
    },
    { distance: metaSearchDistanceToMainOffer },
  )
}

export default useMetaSearchDistance
